import {
  Button,
  Modal,
  Spinner,
  TextArea,
  Typography,
  Icon,
  SelectMenu,
  SelectMenuOption,
  Switch,
} from '@scale/scaleui';
import { AddUserToFlowErrorType, addUserToFlowErrorTypeToMessage } from '@scale/shared-types';
import { useState } from 'react';
import {
  SignupFlowInfo,
  useAddUsersToFlow,
  useSignupFlowInfo,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { AddContributorResultsLink } from './AddContributorResultsLink';

const signupFlowInfoToOption = (signupFlowInfo: SignupFlowInfo): SelectMenuOption<string> => ({
  id: signupFlowInfo._id,
  name: `${signupFlowInfo.name} (token: ${signupFlowInfo.token})`,
});

interface AddWorkerModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowName: string;
  flowId: string;
}

const errorTypeToMessage = (type: AddUserToFlowErrorType, count: number) => {
  return `${count} users in this batch will not be added due to ${addUserToFlowErrorTypeToMessage[type]}`;
};

export const AddWorkerModal: React.FC<AddWorkerModalProps> = ({
  isOpen,
  onClose,
  flowName,
  flowId,
}) => {
  const [contributors, setContributors] = useState<string>('');
  const [shouldAddToSignupFlow, setShouldAddToSignupFlow] = useState<boolean>(false);
  const [selectedSignupFlow, setSelectedSignupFlow] = useState<SignupFlowInfo | null>(null);
  const { isLoading: isLoadingSignupFlowInfo, data: signupFlowInfo } = useSignupFlowInfo(
    flowId?.startsWith('tempId_') ? undefined : flowId,
    (data: SignupFlowInfo[]) => {
      if (data.length > 0) setSelectedSignupFlow(data[0]);
    },
  );

  const { mutate, data, error, isLoading, isError } = useAddUsersToFlow();
  const handleSubmit = () => {
    const ids = contributors.split(',').map(id => id.trim());
    mutate({
      userIds: ids,
      flowId,
      signupFlowId: shouldAddToSignupFlow ? selectedSignupFlow?._id : undefined,
      batchSize: 100,
    });
  };

  return (
    <Modal open={isOpen} onClose={onClose} title={`Add Contributor(s) to ${flowName}`}>
      <Typography.Paragraph3>
        Enter comma-separated Contributor IDs to add to this flow. If contributors are already in a
        flow, they will be moved to this flow. They will get any trigger emails not received in the
        past 2 days.
      </Typography.Paragraph3>
      <TextArea
        className="my-4"
        id="text-area"
        title="Contributors to Add"
        value={contributors}
        onChange={e => setContributors(e.target.value)}
        rows={4}
      />

      <div className="flex flex-row items-center mb-2 w-full">
        <div>
          <Typography.Paragraph2 className="text-medium">Add to Signup Flow</Typography.Paragraph2>
        </div>
        <Switch
          className="ml-2"
          checked={shouldAddToSignupFlow}
          onCheckedChange={setShouldAddToSignupFlow}
        />
      </div>
      {shouldAddToSignupFlow && isLoadingSignupFlowInfo && <Spinner size={'sm'} />}
      {shouldAddToSignupFlow && !isLoadingSignupFlowInfo && signupFlowInfo?.length && (
        <>
          <SelectMenu
            className="mb-4"
            options={signupFlowInfo.map(signupFlowInfoToOption)}
            selectedOptionId={selectedSignupFlow?._id ?? signupFlowInfo[0]._id}
            setSelected={(id: string) =>
              setSelectedSignupFlow(signupFlowInfo.find(flow => flow._id === id) ?? null)
            }
            variant="minimal"
          />
        </>
      )}

      {isError && error && (
        <div className="flex flex-col gap-y-2 my-2">
          {Object.entries(error.errorCounts).map(
            ([type, count]: [string, number], index: number) => (
              <div key={index} className="flex items-center gap-x-2">
                <Icon icon="exclamation-circle" variant="danger" />
                <Typography.Paragraph3 className="text-red-500 m-0 flex-grow">
                  {errorTypeToMessage(type as AddUserToFlowErrorType, count)}
                </Typography.Paragraph3>
              </div>
            ),
          )}
          <div className="flex items-center gap-x-2 mb-2">
            <Typography.Paragraph3 className="text-red-500 m-0 flex-grow">
              {`Successfully added ${error.success} users to flow`}
            </Typography.Paragraph3>
            <Button variant="neutral" size="sm">
              <AddContributorResultsLink csvData={error.csvData} />
            </Button>
          </div>
        </div>
      )}
      <div className="flex justify-end">
        {isLoading && <Spinner className="mr-2" size={'sm'} />}
        <Button disabled={isLoading} onClick={handleSubmit}>
          Submit
        </Button>
      </div>
      {data && (
        <div className="flex flex-col gap-y-2 my-2">
          <Typography.Paragraph3>Successfully added contributors to flow</Typography.Paragraph3>
        </div>
      )}
    </Modal>
  );
};
